'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { motion, useScroll, useSpring } from 'motion/react';
import type { Variants } from 'motion/react';
import { RiArrowLeftLine, RiImageLine, RiInformationLine } from '@remixicon/react';
import { useLocale, useTranslations } from 'next-intl';
import type { BlogBlock, BlogPostDetail } from '@mentorivo/shared';

import { Link } from '@/i18n/navigation';
import { formatDate } from '@/lib/format';
import { dur, ease, spring, springFade, stagger } from '@/lib/motion';

/**
 * Yazı sayfasının okuma yüzeyi — başlık bloğu, içindekiler ve gövde.
 *
 * ── Metin nereden geliyor ─────────────────────────────────────────────────
 *
 * Tamamı prop olarak, sunucudan (`/[locale]/blog/[slug]/page.tsx`). Bu bileşen
 * `useTranslations`'ı yalnızca ARAYÜZ etiketleri için çağırıyor ("Tüm yazılar",
 * "İçindekiler", "dk okuma"); yazının kendisi çeviri dosyasında değil
 * veritabanında (gerekçe `lib/blog.ts`).
 *
 * ── Gövde neden `dangerouslySetInnerHTML` değil ───────────────────────────
 *
 * Gövde bir blok listesi (`BlogBlock[]`), HTML değil. Her blok tipinin burada
 * TEK bir karşılığı var ve tanınmayan tip hiç basılmıyor. Admin editörü
 * (Faz 9) kullanıcı girdisi üretmeye başladığında bu ayrım bir XSS yüzeyinin
 * hiç açılmaması demek — sözleşmedeki gerekçe `blogBlockSchema` başında.
 *
 * ── Hareket: üç ayrı jest, üçü de bir şey söylüyor ────────────────────────
 *
 *  1. **Okuma çubuğu** (üstte, sayfanın tepesine sabit). Kaydırmanın kendisine
 *     bağlı — süs değil, "yazının neresindeyim" sorusunun cevabı. Uzun bir
 *     metinde bu soru gerçekten soruluyor. Yay ile yumuşatılıyor ki her
 *     kaydırma pikselinde titremesin.
 *  2. **İçindekiler işareti** `layoutId` ile taşınıyor — blog dizinindeki
 *     süzgeç hapıyla aynı dil: "aynı işaret şimdi burada".
 *  3. **Gövde blokları hareketsiz** — yazı açıldığı anda tamamı yerinde
 *     (`initial={false}`). Eskiden görünür olduklarında bir kez oturuyorlardı
 *     (`whileInView` + `once: true`); kaydırdıkça açılan sayfa 9 Eyl 2026'da
 *     kullanıcı kararıyla kaldırıldı.
 *
 * Başlık bloğu sayfanın TEPESİNDE `animate` ile açılmaya devam ediyor —
 * `page-head.tsx` ve `blog-hero.tsx` ile aynı gerekçe.
 *
 * ── İçindekiler dar ekranda ───────────────────────────────────────────────
 *
 * DOM'da HER ZAMAN var, CSS ile gizleniyor (`.art-toc`). JavaScript ile
 * ekran genişliğine bakıp koşullu render etmek hidrasyon uyuşmazlığı
 * üretiyor — bu depoda bir kez yaşandı ve kural o günden beri aynı: her zaman
 * bas, görünürlüğü CSS'e bırak.
 */

/* Başlık bloğu: okuma sırası. Geri bağlantısı → üst bilgi → başlık → özet →
   künye → kapak. */
const headV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base, delayChildren: 0.05 } },
};

const lineV: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

/* Başlık tek öğe olduğu için blur'a yer var (DESIGN.md §6 bütçesi: aynı anda
   ~15 öğe). Hero'ların imzası, burada da açılışı işaretliyor. */
const titleV: Variants = {
  hidden: { opacity: 0, y: 16, filter: 'blur(6px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: springFade(spring.default, dur.base),
  },
};

const coverV: Variants = {
  hidden: { opacity: 0, scale: 0.985 },
  show: { opacity: 1, scale: 1, transition: springFade(spring.soft) },
};

/* Gövde bloğu. Mesafe kısa (8px) ve yay `snap`: paragraf okunacak bir şey,
   sahnelenecek bir şey değil — geç gelen bir metin okumayı geciktirir. */
const blockV: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: springFade(spring.snap) },
};

const tocV: Variants = {
  hidden: { opacity: 0, x: 8 },
  show: { opacity: 1, x: 0, transition: { duration: dur.base, ease: ease.out } },
};

/**
 * Etkin başlığı bulmanın bandı.
 *
 * Üstte header payı kadar (`--header-h`, en fazla 76px) ölü alan var; altta
 * ekranın %65'i. Yani bir başlık ekranın üst dörtte birine girdiğinde etkin
 * sayılıyor — kullanıcının "şu an burayı okuyorum" dediği yer orası.
 * Band olmadan (`threshold` ile) iki başlık aynı anda görünür olduğunda işaret
 * ikisi arasında zıplıyordu.
 */
const TOC_BAND = '-88px 0px -65% 0px';

export function BlogArticle({ post }: { post: BlogPostDetail }) {
  const t = useTranslations('blog');
  const locale = useLocale();
  const uid = useId();

  const bodyRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<string>('');

  /* Çubuk gövdenin BOYUNA bağlı, sayfanınkine değil: başlık bloğu ve alttaki
     ilgili yazılar okuma değil, dolayısıyla ilerlemeye de dahil değil.
     Kullanıcı gövdenin sonuna geldiğinde çubuk dolu olmalı. */
  const { scrollYProgress } = useScroll({
    target: bodyRef,
    offset: ['start start', 'end end'],
  });

  /* `spring.snap`in sayıları — `type` alanı `useSpring`in beklediği yapıda
     yok. Ham sayı yazmak yerine token'dan sıyırmak, DESIGN.md §5'in "değerler
     bileşenlerde tekrarlanmaz" kuralını koruyor. */
  const { type: _snapType, ...snapConfig } = spring.snap;
  const progress = useSpring(scrollYProgress, snapConfig);

  /* Etkin başlık. `IntersectionObserver` seçildi çünkü alternatifi her
     kaydırma karesinde `getBoundingClientRect()` okumak — beş başlık için
     bile gereksiz bir düzen hesabı zinciri. */
  useEffect(() => {
    if (post.toc.length === 0) return;

    const targets = post.toc
      .map((entry) => document.getElementById(entry.anchor))
      .filter((el): el is HTMLElement => el !== null);

    if (targets.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const inBand = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        // Banda hiçbir başlık girmediğinde ETKİN OLANI BIRAKIYORUZ: son
        // başlığın altındaki paragrafları okurken işaretin sönmesi, "artık
        // hiçbir bölümde değilim" demek olurdu.
        if (inBand[0]) setActive(inBand[0].target.id);
      },
      { rootMargin: TOC_BAND, threshold: 0 },
    );

    for (const target of targets) observer.observe(target);
    return () => observer.disconnect();
  }, [post.toc]);

  return (
    <article className="art">
      {/* Okuma çubuğu. `transform-origin` solda: çubuk soldan doluyor, ortadan
          değil. `aria-hidden` çünkü aynı bilgi ekran okuyucuya kaydırma
          konumuyla zaten iletiliyor; ikinci bir canlı bölge gürültü olurdu. */}
      <motion.div className="art-progress" style={{ scaleX: progress }} aria-hidden="true" />

      {/* Header `position: fixed` — akışta yer KAPLAMIYOR ve ayrık kabuk
          header kutusunun altına taşıyor (14 + 72 = 86px). Pay verilmediğinde
          "Tüm yazılar" satırı kabuğun altına giriyordu. Ölçü `--header-h`ten
          değil kabuğun alt kenarından: `.page-head-space` ve `.bhero-space`
          ile aynı hesap, aynı değişkenler. */}
      <div className="art-space" aria-hidden="true" />

      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <div className="art-frame">
            <motion.div className="art-head" variants={headV} initial="hidden" animate="show">
              <motion.p className="art-back" variants={lineV}>
                <Link className="art-back-link" href="/blog">
                  <RiArrowLeftLine size={16} aria-hidden="true" />
                  {t('post.back')}
                </Link>
              </motion.p>

              <motion.p className="art-meta" variants={lineV}>
                <span className="art-topic">{t(`topics.${post.topic}`)}</span>
                <span aria-hidden="true">·</span>
                <time dateTime={post.publishedAt}>{formatDate(post.publishedAt, locale)}</time>
                <span aria-hidden="true">·</span>
                <span>{t('list.read', { minutes: post.readMinutes })}</span>
              </motion.p>

              <motion.h1 className="art-title" variants={titleV}>
                {post.title}
              </motion.h1>

              {/* Özet gövdenin bir parçası DEĞİL — kartta duran cümlenin
                  kendisi. Burada tekrarlanması bilinçli: yazıya bağlantıdan
                  gelen okur, kartta okuduğu cümleyi başlığın altında bulunca
                  doğru yere geldiğini anlıyor. */}
              <motion.p className="art-lede" variants={lineV}>
                {post.excerpt}
              </motion.p>

              <motion.p className="art-byline" variants={lineV}>
                <span className="art-author">{post.authorName}</span>
                {post.authorRole ? (
                  <span className="art-author-role">{post.authorRole}</span>
                ) : null}
              </motion.p>

              <motion.div className="art-cover" variants={coverV}>
                {post.coverImage ? (
                  <img
                    className="art-cover-img"
                    src={post.coverImage}
                    alt=""
                    decoding="async"
                    /* `loading="lazy"` YOK: kapak ilk ekranda ve tembel
                       yükleme onu geciktirip düzeni zıplatırdı. */
                  />
                ) : (
                  <span className="shot-empty" aria-hidden="true">
                    <RiImageLine size={30} aria-hidden="true" />
                  </span>
                )}
              </motion.div>
            </motion.div>

            <div className="art-layout">
              {/* Düz `div`: `motion.div` olsaydı varyantları çocuklara
                  DAĞITIRDI ve her blok görünür olmayı beklemeden birden
                  oynardı. Kabın tek işi ölçüm (okuma çubuğu) ve düzen. */}
              <div className="art-body" ref={bodyRef}>
                {post.body.map((block, index) => (
                  <Block key={index} block={block} />
                ))}
              </div>

              {/* Sözleşme iki başlıktan azsa `toc`u BOŞ döndürüyor; o durumda
                  kenar sütunu hiç basılmıyor ve gövde tam genişliğe yayılıyor
                  (CSS: `.art-layout:has(> .art-toc)`). */}
              {post.toc.length > 0 ? (
                <motion.nav
                  className="art-toc"
                  aria-label={t('post.toc')}
                  variants={tocV}
                  initial="hidden"
                  animate="show"
                >
                  <p className="art-toc-title">{t('post.toc')}</p>
                  <ol className="art-toc-list">
                    {post.toc.map((entry) => {
                      const current = entry.anchor === active;

                      return (
                        <li className="art-toc-item" key={entry.anchor}>
                          <a
                            className="art-toc-link"
                            href={`#${entry.anchor}`}
                            aria-current={current ? 'true' : undefined}
                          >
                            {current ? (
                              <motion.span
                                className="art-toc-mark"
                                aria-hidden="true"
                                layoutId={`${uid}-toc`}
                                transition={spring.default}
                              />
                            ) : null}
                            {/* Metin kendi katmanında: işaret `absolute` ve
                                sarmalayıcı olmadan metnin ÜSTÜNE binerdi
                                (dizindeki `.posts-chip-text` ile aynı
                                kurulum ve aynı gerekçe). */}
                            <span className="art-toc-text">{entry.text}</span>
                          </a>
                        </li>
                      );
                    })}
                  </ol>
                </motion.nav>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

/**
 * Tek blok.
 *
 * `switch` tam kapsayıcı: sözleşmeye yeni bir blok tipi eklendiğinde
 * TypeScript burada `never` uyumsuzluğuyla duruyor. Bu bilinçli — yeni tipin
 * ekranda karşılığı olmadan sözleşmeye girmesini engelliyor.
 */
function Block({ block }: { block: BlogBlock }) {
  switch (block.type) {
    case 'heading':
      return (
        <motion.h2
          className="art-h2"
          /* Çıpa sunucuda türetiliyor (`blog.mappers.ts`); burada yeniden
             hesaplansaydı içindekiler bağlantısıyla ayrılabilirdi. */
          id={block.anchor}
          variants={blockV}
          initial={false}
          animate="show"
        >
          {block.text}
        </motion.h2>
      );

    case 'subheading':
      return (
        <motion.h3 className="art-h3" variants={blockV} initial={false} animate="show">
          {block.text}
        </motion.h3>
      );

    case 'paragraph':
      return (
        <motion.p className="art-p" variants={blockV} initial={false} animate="show">
          {block.text}
        </motion.p>
      );

    case 'list': {
      const items = block.items.map((item, index) => (
        <li className="art-li" key={index}>
          {item}
        </li>
      ));

      return block.ordered ? (
        <motion.ol
          className="art-list art-list--ordered"
          variants={blockV}
          initial={false}
          animate="show"
        >
          {items}
        </motion.ol>
      ) : (
        <motion.ul className="art-list" variants={blockV} initial={false} animate="show">
          {items}
        </motion.ul>
      );
    }

    case 'quote':
      return (
        <motion.figure className="art-quote" variants={blockV} initial={false} animate="show">
          <blockquote className="art-quote-text">{block.text}</blockquote>
          {block.cite ? <figcaption className="art-quote-cite">{block.cite}</figcaption> : null}
        </motion.figure>
      );

    case 'note':
      /* İkon kutu İÇİNDE değil, doğrudan zemine oturuyor — DESIGN.md §3'ün
         kesinleşmiş kararı. Notun zemini ve sol hattı kabın kendisinde. */
      return (
        <motion.aside className="art-note" variants={blockV} initial={false} animate="show">
          <RiInformationLine className="art-note-icon" size={18} aria-hidden="true" />
          <div className="art-note-text">
            {block.title ? <p className="art-note-title">{block.title}</p> : null}
            <p className="art-note-body">{block.text}</p>
          </div>
        </motion.aside>
      );

    case 'image':
      return (
        <motion.figure className="art-figure" variants={blockV} initial={false} animate="show">
          <img
            className="art-figure-img"
            src={block.src}
            alt={block.alt}
            loading="lazy"
            decoding="async"
          />
          {block.caption ? (
            <figcaption className="art-figure-cap">{block.caption}</figcaption>
          ) : null}
        </motion.figure>
      );

    case 'gallery':
      /* Dış `<figure>` grubu, iç `<figure>`ler tek tek görselleri sarıyor —
         HTML'in kendi kalıbı (spec'teki "gruplanmış görseller" örneği).
         Sarmalayıcı `<div>` olsaydı altyazılar hangi görsele ait olduğunu
         yalnızca konumla söylerdi. */
      return (
        <motion.figure
          className="art-gallery"
          data-count={block.images.length}
          variants={blockV}
          initial={false}
          animate="show"
        >
          {block.images.map((image, index) => (
            <figure className="art-gallery-item" key={index}>
              <img
                className="art-gallery-img"
                src={image.src}
                alt={image.alt}
                loading="lazy"
                decoding="async"
              />
              {image.caption ? (
                <figcaption className="art-gallery-cap">{image.caption}</figcaption>
              ) : null}
            </figure>
          ))}
        </motion.figure>
      );

    case 'divider':
      /* Ayraç `<hr>`: gerçekten bir konu değişimi işareti, dekoratif bir çizgi
         değil. Ekran okuyucu da onu ayraç olarak duyuruyor. */
      return <motion.hr className="art-divider" variants={blockV} initial={false} animate="show" />;
  }
}
